import { Container, Typography, Paper, Box, Avatar, Stack, Tab, Tabs, Button, Link as MuiLink } from "@mui/material";
import { useEffect, useState } from "react";
import { getMyProfile, getMyPosts, getMyComments, getMyUpvotedPosts, UserProfile } from "../api/profile";
import { Post } from "../api/posts";
import { Comment } from "../api/comments";
import PostCard from "../components/PostCard";
import { getMyVotes } from "../api/votes";
import { useNavigate, Link } from "react-router-dom";
import { clearToken } from "../api/client";

export default function ProfilePage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [upvotedPosts, setUpvotedPosts] = useState<Post[]>([]);
  const [myVotes, setMyVotes] = useState<Record<number, number>>({}); // For PostCard
  const [error, setError] = useState("");

  async function loadProfile() {
      try {
          setError("");
          const p = await getMyProfile();
          setProfile(p);
      } catch (e: any) {
          console.error(e);
          const msg = e.message || "Failed to load profile";
          if (msg.includes("user not found") || msg.includes("404")) {
             clearToken();
             navigate("/login");
             return;
          }
          setError(msg);
      }
  }

  async function loadData() {
      if (!profile) return;
      try {
        // Load votes map for PostCards
        const raw = await getMyVotes();
        const mapped: Record<number, number> = {};
        for (const [k, v] of Object.entries(raw)) mapped[Number(k)] = v;
        setMyVotes(mapped);

        if (tab === 0) {
            const data = await getMyPosts();
            setPosts(data || []);
        } else if (tab === 1) {
            const data = await getMyComments();
            setComments(data || []);
        } else if (tab === 2) {
            const data = await getMyUpvotedPosts();
            setUpvotedPosts(data || []);
        }
      } catch (e) {
          console.error("Failed to load data", e);
      }
  }

  // ...

  useEffect(() => {
      loadProfile();
  }, []);

  useEffect(() => {
    if (profile) {
        loadData();
    }
  }, [tab, profile]);


  if (error) return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
          <Typography color="error" variant="h6">{error}</Typography>
          <Button onClick={loadProfile} sx={{ mt: 2 }}>Retry</Button>
      </Container>
  );

  if (!profile) return <Container sx={{ mt: 4 }}>Loading...</Container>;

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, mb: 4, textAlign: 'center' }}>
        <Stack alignItems="center" spacing={2}>
            <Avatar 
                sx={{ width: 100, height: 100, bgcolor: 'primary.main', fontSize: '3rem' }}
            >
                {profile.username[0].toUpperCase()}
            </Avatar>
            <Box textAlign="center">
                <Typography variant="h4" fontWeight={800}>{profile.username}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Joined {new Date(profile.created_at).toLocaleDateString()}
                </Typography>
                
                <Stack direction="row" spacing={3} justifyContent="center">
                    <Box>
                        <Typography variant="h6" fontWeight={700}>{profile.post_count}</Typography>
                        <Typography variant="caption" color="text.secondary">Posts</Typography>
                    </Box>
                    <Box>
                        <Typography variant="h6" fontWeight={700}>{profile.comment_count}</Typography>
                        <Typography variant="caption" color="text.secondary">Comments</Typography>
                    </Box>
                    <Box>
                        <Typography variant="h6" fontWeight={700}>{profile.vote_count}</Typography>
                        <Typography variant="caption" color="text.secondary">Upvotes Given</Typography>
                    </Box>
                </Stack>
            </Box>
        </Stack>
      </Paper>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab label="My Posts" sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tab label="My Comments" sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tab label="Upvoted" sx={{ textTransform: 'none', fontWeight: 600 }} />
        </Tabs>
      </Box>

      {/* Posts Tab */}
      {tab === 0 && (
          <Stack spacing={2}>
              {posts.length === 0 ? <Typography color="text.secondary" align="center">No posts yet.</Typography> : 
                posts.map(p => (
                    <PostCard key={p.post_id} post={p} myVote={myVotes[p.post_id] ?? 0} onVoted={loadData} />
                ))
              }
          </Stack>
      )}

       {/* Comments Tab */}
       {tab === 1 && (
          <Stack spacing={2}>
               {comments.length === 0 ? <Typography color="text.secondary" align="center">No comments yet.</Typography> : 
                comments.map(c => (
                    <Paper key={c.comment_id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            Replying to: <MuiLink component={Link} to={`/posts/${c.post_id}`} underline="hover" sx={{ fontWeight: 700 }}>
                                {c.post_title || `Post #${c.post_id}`}
                            </MuiLink>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {c.content}
                        </Typography>
                         <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            Score: {c.vote_score} • {new Date(c.created_at).toLocaleDateString()}
                        </Typography>
                    </Paper>
                ))
              }
          </Stack>
      )}

       {/* Upvoted Tab */}
       {tab === 2 && (
          <Stack spacing={2}>
              {upvotedPosts.length === 0 ? <Typography color="text.secondary" align="center">No upvoted posts yet.</Typography> : 
                upvotedPosts.map(p => (
                    <PostCard key={p.post_id} post={p} myVote={myVotes[p.post_id] ?? 0} onVoted={loadData} />
                ))
              }
          </Stack>
      )}
    </Container>
  );
}
